// entityRef — THE one shared contract for "a pointer at a workspace entity"
// (VaultOS v2 design §2). Every surface that has to name a record, document,
// mail thread, ticket, file, event, chat channel or canvas board keys on this
// shape, and on ONE string form:
//
//   EntityRef  = { type, id, title?, kind?, collection?, meta? }
//   type       ∈ record | doc | mail | ticket | file | event | channel | canvas
//   string     = 'ref:<type>:<id>'
//
// Per-type `id` grammar (the parser does NOT enforce it — it only splits the
// string form; a malformed id degrades to a ref whose href is null):
//   record → '<collectionKey>/<recordId>'   'vault.items/i-lb-contract-1'
//   mail   → '<threadId>' or '<threadId>#<messageId>' for a single message
//   canvas → '<projectId>/<boardPageId>'
//
// It lives in shared/functions for the same reason dragRef.js does: BOTH ends
// cross package boundaries. Producers are canvas items, AI source trailers,
// comment threads and module pages; consumers are the assistant renderer, the
// canvas entity card and the comment thread. Packages must never import from
// each other (FA001) — every consumer goes through
// `el.call('parseEntityRef' | 'formatEntityRef' | 'entityRefHref' |
// 'entityRefIcon' | 'isEntityRef', …)`.
//
// The server carries a mirror of the PARSE/FORMAT half at
// `server/src/core/utils/entityRef.js` under the same names. Hrefs are a
// client-route concern and live only here.
//
// ── FA204 / frank trap ──────────────────────────────────────────────────────
// Every function below is SELF-CONTAINED: it inlines its own constants and
// never reads a module-scope const or calls a sibling export by its bare name.
// frank.toJSON serializes each exported function on its own and does NOT
// rewrite free vars inside hoisted helper bodies, so a bare sibling read is
// `undefined` at runtime in a published consumer. dragRef.js in this same
// folder is organised the identical way — keep both that way.

// The eight types this contract defines. Exported for producers that want to
// validate before minting (the canvas picker, the server allowlist mirror).
// Anything outside the list still PARSES — unknown types degrade to a null
// href and the generic glyph rather than throwing, exactly like
// DRAG_REF_KINDS' unknown-kind rule.
export const ENTITY_REF_TYPES = [
  'record',
  'doc',
  'mail',
  'ticket',
  'file',
  'event',
  'channel',
  'canvas'
]

// Parse anything that can name an entity into the canonical object shape, or
// null when it names nothing.
//
// Accepts, in order:
//   • 'ref:<type>:<id>'            → { type, id }
//   • { type, id, … }              → normalized copy (extra keys preserved)
//   • { ref: 'ref:<type>:<id>', … } → parsed string + the object's own fields
//     (this is the SOURCES trailer shape: `{ ref, use }` — see spec §4.2)
//
// `id` keeps every character after the SECOND colon, so an id that itself
// contains ':' survives a round trip.
//
// ── ONE NORMALISATION, AND ONLY ONE ───────────────────────────────────────
// A `record` id is '<collectionKey>/<recordId>', and the SERVER lower-cases
// the COLLECTION KEY inside the ref string (server/src/core/utils/entityRef.js,
// L1 2026-09-07). The client does the same, on the same segment only, so a ref
// this side mints is byte-identical to the one the server verifies a citation
// against — `ref:record:Vault.Items/i-1` and `ref:record:vault.items/i-1` are
// the same entity and must be the same STRING, or the citation silently fails
// to verify and the answer loses its grounding with no error anywhere.
//
// The RECORD ID half keeps its case: it is an opaque row id (Mongo ids are
// case-sensitive hex, and a module is free to use any id scheme), so folding it
// would break the read. Nothing else is touched — every other type's id is
// opaque end to end.
export const parseEntityRef = (value) => {
  if (!value) return null
  if (typeof value === 'string') {
    const raw = value.trim()
    if (raw.slice(0, 4) !== 'ref:') return null
    const rest = raw.slice(4)
    const cut = rest.indexOf(':')
    if (cut < 1) return null
    const type = rest.slice(0, cut).trim()
    let id = rest.slice(cut + 1).trim()
    if (!type || !id) return null
    // Inlined, not a shared helper — see the FA204 note in the file header.
    if (type === 'record') {
      const slash = id.indexOf('/')
      if (slash > 0) id = id.slice(0, slash).toLowerCase() + id.slice(slash)
    }
    return { type, id }
  }
  if (typeof value !== 'object') return null
  // `{ ref: 'ref:…' }` — the trailer/source shape. Parse the string leg first,
  // then let the object's own fields ride along (title/use/meta/verified…).
  if (!value.type && typeof value.ref === 'string') {
    const raw = value.ref.trim()
    if (raw.slice(0, 4) !== 'ref:') return null
    const rest = raw.slice(4)
    const cut = rest.indexOf(':')
    if (cut < 1) return null
    const type = rest.slice(0, cut).trim()
    let id = rest.slice(cut + 1).trim()
    if (!type || !id) return null
    if (type === 'record') {
      const slash = id.indexOf('/')
      if (slash > 0) id = id.slice(0, slash).toLowerCase() + id.slice(slash)
    }
    const out = { ...value, type, id }
    delete out.ref
    return out
  }
  const type = value.type == null ? '' : String(value.type).trim()
  let id = value.id == null ? '' : String(value.id).trim()
  if (!type || !id) return null
  if (type === 'record') {
    const slash = id.indexOf('/')
    if (slash > 0) id = id.slice(0, slash).toLowerCase() + id.slice(slash)
  }
  return { ...value, type, id }
}

// The string form. Accepts the same three inputs as parseEntityRef and always
// returns a STRING — '' when the input names nothing, so a caller can use it
// as a map key without a null branch.
export const formatEntityRef = (ref) => {
  if (!ref) return ''
  // Self-contained parse (does NOT call the sibling parseEntityRef — see the
  // FA204 note in the file header).
  let type = ''
  let id = ''
  if (typeof ref === 'string') {
    const raw = ref.trim()
    if (raw.slice(0, 4) !== 'ref:') return ''
    const rest = raw.slice(4)
    const cut = rest.indexOf(':')
    if (cut < 1) return ''
    type = rest.slice(0, cut).trim()
    id = rest.slice(cut + 1).trim()
  } else if (typeof ref === 'object') {
    if (!ref.type && typeof ref.ref === 'string') {
      // Inlined, NOT a recursive `formatEntityRef(ref.ref)` call — a
      // module-scope const read from inside its own body is the same FA204
      // trap as any other sibling read (see the file header).
      const raw = ref.ref.trim()
      if (raw.slice(0, 4) !== 'ref:') return ''
      const rest = raw.slice(4)
      const cut = rest.indexOf(':')
      if (cut < 1) return ''
      type = rest.slice(0, cut).trim()
      id = rest.slice(cut + 1).trim()
    } else {
      type = ref.type == null ? '' : String(ref.type).trim()
      id = ref.id == null ? '' : String(ref.id).trim()
    }
  } else {
    return ''
  }
  if (!type || !id) return ''
  // Same single normalisation as parseEntityRef, inlined for the same FA204
  // reason: the string form is what travels, so it is what has to agree with
  // the server byte for byte.
  if (type === 'record') {
    const slash = id.indexOf('/')
    if (slash > 0) id = id.slice(0, slash).toLowerCase() + id.slice(slash)
  }
  return 'ref:' + type + ':' + id
}

// True when `value` names an entity — i.e. parses to a non-empty type + id.
// Deliberately NOT a type-allowlist check: an older bundle must not reject a
// ref minted by a newer producer (the DRAG_REF_KINDS unknown-kind rule).
export const isEntityRef = (value) => {
  if (!value) return false
  if (typeof value === 'string') {
    const raw = value.trim()
    if (raw.slice(0, 4) !== 'ref:') return false
    const rest = raw.slice(4)
    const cut = rest.indexOf(':')
    if (cut < 1) return false
    return !!rest.slice(0, cut).trim() && !!rest.slice(cut + 1).trim()
  }
  if (typeof value !== 'object') return false
  if (!value.type && typeof value.ref === 'string') {
    const raw = value.ref.trim()
    if (raw.slice(0, 4) !== 'ref:') return false
    const rest = raw.slice(4)
    const cut = rest.indexOf(':')
    if (cut < 1) return false
    return !!rest.slice(0, cut).trim() && !!rest.slice(cut + 1).trim()
  }
  const type = value.type == null ? '' : String(value.type).trim()
  const id = value.id == null ? '' : String(value.id).trim()
  return !!type && !!id
}

// The in-app route that OPENS this entity, or null when the entity has no
// page in THIS workspace.
//
// `meta.href` always wins. It is the escape hatch for a producer that knows
// the real destination better than a generic map can.
// The same override works for every type — a module that mounts
// documents on its own route hands its href down instead of forking this map.
//
// ── `record` — the MODULE RECORD-ROUTE REGISTRY (design §3.4) ──────────────
// A workspace RECORD has no shell page at all: its only home is whatever
// module page owns its collection. So the module DECLARES that, in its own
// manifest, beside the collections it already declares:
//
//   collections: [{ key: 'vault.items', route: '/vault-rooms', param: 'item' }]
//
// The shell reads those rows (`workspaceProjects.js _normalizeRecordRoutes`),
// publishes them on `root.workspaceProject.recordRoutes` and on each
// `root.workspaceApps[].recordRoutes`, and this function consults them. A
// collection NO installed module claims still returns null — the shell never
// invents a route, and no module name appears anywhere in it.
//
// `root` is the ROOT STATE. It is optional in both directions: an explicit
// second argument wins, and a caller that passes none still resolves when it
// reached here through `el.call('entityRefHref', ref)` — el.call binds `this`
// to the element (smbls methods.js `method.call(this, ...args)`). With
// neither, `record` behaves exactly as it did before the registry existed.
//
// The registry read below is INLINED, like every other helper in this file
// (see the FA204 note in the header) — and packages/shared must not import
// from the shell in any case. The shell's own builder,
// `workspace/functions/moduleRecordRoutes.js`, is the canonical union; the two
// are pinned to each other by
// `packages/workspace/__tests__/moduleRecordRoutes.test.js`, which runs BOTH
// against one root and requires them to agree. Change one, change both.
//
// A `mail` id may address ONE message ('<threadId>#<messageId>'); the thread
// is what the route opens, so the fragment is dropped.
//
// ── EVERY ROUTE BELOW IS ONE THE SHELL ACTUALLY SERVES ────────────────────
// Checked against `packages/workspace/pages/index.js`'s route table and
// against the app's OWN deep-link builder, `packages/workspace/functions/
// search/globalSearch.js` `_SURFACE_HREF` — the map the global-search
// dropdown navigates with. Where the two could disagree, this map copies
// that one, because it is the form the product already proves every day.
//
//   doc  → '/notes?id=<id>'    NOT '/documents?doc='. '/documents' is
//          registered to `notesRedirect`, which rewrites the path to /notes
//          and carries the query ALONG UNCHANGED — so a `?doc=` param
//          arrives at a page that only ever reads `?id=` (documents/
//          functions/openSlotDocId.js) and the document never opens. The
//          redirect made the old value look plausible and fail silently.
//   file → '/files?node=<id>'  NOT '?file='. `?node=` is the /files
//          desktop's universal deep-open (files/pages/files.js).
//
// (Decision taken by the VaultOS v2 orchestrator, 2026-09-07; spec §2 and
// `docs/AI_ATTACH_SOURCES_CONFIDENCE.md` carry the same two values.)
//
// A `canvas` ref only resolves with `meta.owner` + `meta.key` — the project
// path is owner/key, and the ref's own id is '<projectId>/<boardPageId>',
// which carries the id, not the slug pair. No meta → null (the canvas card
// then falls back to `focusCanvasEntity`, spec §4.6).
export function entityRefHref(ref, root) {
  if (!ref) return null
  // Self-contained parse — see the FA204 note in the file header.
  let type = ''
  let id = ''
  let meta = null
  if (typeof ref === 'string') {
    const raw = ref.trim()
    if (raw.slice(0, 4) !== 'ref:') return null
    const rest = raw.slice(4)
    const cut = rest.indexOf(':')
    if (cut < 1) return null
    type = rest.slice(0, cut).trim()
    id = rest.slice(cut + 1).trim()
  } else if (typeof ref === 'object') {
    if (!ref.type && typeof ref.ref === 'string') {
      const raw = ref.ref.trim()
      if (raw.slice(0, 4) !== 'ref:') return null
      const rest = raw.slice(4)
      const cut = rest.indexOf(':')
      if (cut < 1) return null
      type = rest.slice(0, cut).trim()
      id = rest.slice(cut + 1).trim()
    } else {
      type = ref.type == null ? '' : String(ref.type).trim()
      id = ref.id == null ? '' : String(ref.id).trim()
    }
    meta = ref.meta && typeof ref.meta === 'object' ? ref.meta : null
  } else {
    return null
  }
  if (!type || !id) return null
  if (meta && typeof meta.href === 'string' && meta.href) return meta.href
  const enc = (v) => encodeURIComponent(String(v))
  if (type === 'record') {
    // '<collectionKey>/<recordId>' — the collection segment is already
    // lower-cased by the parse above, and the record id keeps its case.
    const slash = id.indexOf('/')
    if (slash < 1) return null
    const collection = id.slice(0, slash).toLowerCase()
    const recordId = id.slice(slash + 1)
    if (!collection || !recordId) return null
    let src = root && typeof root === 'object' ? root : null
    if (!src && this && typeof this === 'object' && typeof this.getRootState === 'function') {
      try {
        src = this.getRootState()
      } catch (_) {
        src = null
      }
    }
    if (!src || typeof src !== 'object') return null
    // The takeover module's rows first, then every additive app's — first
    // declaration of a key wins, exactly the order `liveFileHandlers` uses so
    // an installed app can never shadow the workspace's own module.
    const lists = []
    const proj = src.workspaceProject
    if (proj && typeof proj === 'object' && Array.isArray(proj.recordRoutes)) {
      lists.push(proj.recordRoutes)
    }
    if (Array.isArray(src.workspaceApps)) {
      for (const app of src.workspaceApps) {
        if (app && typeof app === 'object' && Array.isArray(app.recordRoutes)) {
          lists.push(app.recordRoutes)
        }
      }
    }
    for (const rows of lists) {
      for (const row of rows) {
        if (!row || typeof row !== 'object') continue
        const rowKey = row.key == null ? '' : String(row.key).trim().toLowerCase()
        if (rowKey !== collection) continue
        const route = row.route == null ? '' : String(row.route).trim()
        const param = row.param == null ? '' : String(row.param).trim()
        // A half-declared row can never mint a href — the shell would
        // otherwise navigate somewhere blank and wait for a page that is
        // never coming (same rule `_normalizeOpens` applies to ext + entry).
        if (!route || route.charAt(0) !== '/' || !param) continue
        return route + '?' + param + '=' + enc(recordId)
      }
    }
    return null
  }
  if (type === 'doc') return '/notes?id=' + enc(id)
  if (type === 'mail') {
    const hash = id.indexOf('#')
    return '/mail?thread=' + enc(hash === -1 ? id : id.slice(0, hash))
  }
  if (type === 'ticket') return '/tasks?ticket=' + enc(id)
  if (type === 'file') return '/files?node=' + enc(id)
  if (type === 'event') return '/calendar?event=' + enc(id)
  if (type === 'channel') return '/chat?channel=' + enc(id)
  if (type === 'canvas') {
    const owner = meta && meta.owner ? String(meta.owner) : ''
    const key = meta && meta.key ? String(meta.key) : ''
    if (!owner || !key) return null
    const slash = id.indexOf('/')
    const boardPageId = slash === -1 ? '' : id.slice(slash + 1)
    return '/' + enc(owner) + '/' + enc(key) + (boardPageId ? '?board=' + enc(boardPageId) : '')
  }
  return null
}

// Icon name per type, for the chip a consumer renders.
//
// Every name below is VERIFIED against the merged registry (brand
// designSystem/icons.js ∪ workspace designSystem/icons.js). `document` and not
// `file` for the file type, and `email` and not `mail`, for the same reason
// dragRefIcon documents: an unresolved name degrades to a blank glyph rather
// than throwing, so a wrong name ships as a silently empty chip.
// Unknown types get the generic document glyph.
export const entityRefIcon = (ref) => {
  // FA204 / frank trap — inlined (self-contained local), see the file header.
  const TYPE_ICONS = {
    record: 'database',
    doc: 'document',
    mail: 'email',
    ticket: 'checkOval',
    file: 'document',
    event: 'dateOutline',
    channel: 'chat',
    canvas: 'grid'
  }
  let type = ''
  if (typeof ref === 'string') {
    const raw = ref.trim()
    if (raw.slice(0, 4) === 'ref:') {
      const rest = raw.slice(4)
      const cut = rest.indexOf(':')
      if (cut > 0) type = rest.slice(0, cut).trim()
    }
  } else if (ref && typeof ref === 'object') {
    if (!ref.type && typeof ref.ref === 'string') {
      const raw = ref.ref.trim()
      if (raw.slice(0, 4) === 'ref:') {
        const rest = raw.slice(4)
        const cut = rest.indexOf(':')
        if (cut > 0) type = rest.slice(0, cut).trim()
      }
    } else if (ref.type != null) {
      type = String(ref.type).trim()
    }
  }
  return TYPE_ICONS[type] || 'document'
}

// The polyglot KEY for this ref's TYPE — "Record", "Thread", "Document"…
//
// §3.5 "never a dead card": a producer that sent no title must not have its
// citation printed as a machine string. The Longbow board carried a node
// titled "Mail thread f82cc8" — a hex id where a human expects a name — and
// an assistant source chip did the same with `<collection>/<rowId>`. The kind
// is the honest fallback: it says WHAT the thing is when nothing said WHICH.
//
// Returns a KEY, not a word: the caller resolves it at its render site
// (`el.call('polyglot', key)`), which is the only place a language is known.
// Every key is a LITERAL in the map below — an assembled key
// ('ai.sources.kind.' + type) is invisible to grep, to the parity test and to
// every extraction tool (docs/I18N-CONVENTION.md §1.3).
//
// An unknown type resolves to the generic label rather than throwing — the
// same rule entityRefIcon and DRAG_REF_KINDS follow, so an older bundle never
// hard-fails on a ref a newer producer minted.
export const entityRefKindKey = (ref) => {
  // FA204 / frank trap — inlined (self-contained local), see the file header.
  const TYPE_KEYS = {
    record: 'ai.sources.kind.record',
    doc: 'ai.sources.kind.doc',
    mail: 'ai.sources.kind.mail',
    ticket: 'ai.sources.kind.ticket',
    file: 'ai.sources.kind.file',
    event: 'ai.sources.kind.event',
    channel: 'ai.sources.kind.channel',
    canvas: 'ai.sources.kind.canvas'
  }
  let type = ''
  if (typeof ref === 'string') {
    const raw = ref.trim()
    if (raw.slice(0, 4) === 'ref:') {
      const rest = raw.slice(4)
      const cut = rest.indexOf(':')
      if (cut > 0) type = rest.slice(0, cut).trim()
    }
  } else if (ref && typeof ref === 'object') {
    if (!ref.type && typeof ref.ref === 'string') {
      const raw = ref.ref.trim()
      if (raw.slice(0, 4) === 'ref:') {
        const rest = raw.slice(4)
        const cut = rest.indexOf(':')
        if (cut > 0) type = rest.slice(0, cut).trim()
      }
    } else if (ref.type != null) {
      type = String(ref.type).trim()
    }
  }
  return TYPE_KEYS[type] || 'ai.sources.kind.unknown'
}
